import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import t from '../../../labels/labelling-utils';
import { IComponent } from '../../../typings/generic-component';
import { MatchPhase, nonLinearCompetitionPhasesArray, MatchPhaseRound, matchPhaseArray } from '../../../typings/match';
import { isEmptyArray, isNotEmptyArray } from '../../../utils/array';
import { isPropTypeNullable } from '../../../utils/prop-types';
import ErrorComponent from '../../atoms/error/error';
import Hr from '../../atoms/hr/hr';
import NegativeMargin from '../../atoms/negative-margin/negative-margin';
import MatchRow from '../../molecules/match-row/match-row';
import AdditiveFilterComponent, { IAdditiveFilterModel } from '../../organisms/additive-filter/additive-filter';
import ResultsTable, { IMatchDayList } from '../../organisms/results-table/results-table';
import ErrorBoundary from '../error-boundary/error-boundary';
import { IClassNames, makeClassNames } from '../../../utils/styles';

export interface ITeamCompetition extends IMatchDayList {
  customCompetitionName: string;
  month: string;
}

export interface ITeamCalendarResults extends IComponent {
  competitionFilterModel?: Array<IAdditiveFilterModel>;
  competitionList: Array<ITeamCompetition>;
  teamId?: string | number; // FIXME: is it useful for this DS component? See livedata-football-widgets/src/widgets/team-calendar-results/transformer/query-transformer.ts
}

interface ISelectedCompetitionAndMonth {
  competitionName: string;
  monthName: string;
  competitionIndex: number;
  monthIndex: number;
}

const getErrorComponent = () => <ErrorComponent>{t('unavailable.data')}</ErrorComponent>;

const isValidCompetitionList = (list: Array<ITeamCompetition>): Boolean => Boolean(list && list.length);

const getUpdatedCompetitionList = (
  list: Array<ITeamCompetition>,
  { competitionName, monthName, competitionIndex, monthIndex }: ISelectedCompetitionAndMonth
): Array<ITeamCompetition> =>
  list
    .filter((competition: ITeamCompetition) =>
      competitionIndex
        ? competition.customCompetitionName === competitionName || competition.month === competitionName
        : true
    )
    .filter((competition: ITeamCompetition) => (monthIndex ? competition.month === monthName : true));

const getRoundName = (competitionPhase?: MatchPhase, round?: number): string =>
  `${competitionPhase || (round ? `G${round}` : '')}${competitionPhase || round ? ' - ' : ''}`;

const classNames = (): IClassNames =>
  makeClassNames({
    teamCalendarResults: 'team-calendar-results',
  });

const TeamCalendarResults: React.FC<ITeamCalendarResults> = ({
  /* istanbul ignore next: default value  */ competitionList = [],
  /* istanbul ignore next: default value  */ competitionFilterModel = [],
  teamId,
  trackingData,
}) => {
  const cn = classNames();
  const [filteredCompetitionList, setFilteredCompetitionList] = useState(competitionList);

  const onChange = (competitionIndex: number, monthIndex: number) => {
    const competitionName = competitionFilterModel[competitionIndex]?.name;
    const monthName = competitionFilterModel[competitionIndex]?.secondaryDropdownModel?.[monthIndex]?.label || '';
    setFilteredCompetitionList(
      getUpdatedCompetitionList(competitionList, { competitionName, monthName, competitionIndex, monthIndex })
    );
  };

  useEffect(() => {
    // Due to useState() not been called correctly on 1st render, "filteredCompetitionList" is empty and "no data" error message is displayed
    // console.log('useEffect', { competitionFilterModel, competitionList, filteredCompetitionList });
    if (
      isNotEmptyArray(competitionFilterModel) &&
      isNotEmptyArray(competitionList) &&
      isEmptyArray(filteredCompetitionList)
    ) {
      setFilteredCompetitionList(competitionList); // or onChange(0, 0)?
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competitionList]); // Called on 1st render only

  return (
    <div className={cn.teamCalendarResults}>
      <ErrorBoundary hasError={!isValidCompetitionList(filteredCompetitionList)} errorComponent={getErrorComponent()}>
        {competitionFilterModel && competitionFilterModel.length ? (
          <React.Fragment>
            <AdditiveFilterComponent
              onChange={onChange}
              filterModel={competitionFilterModel}
              primaryDropdownTitle={t('team.results.filter.competitions')}
              secondaryDropdownTitle={t('team.results.filter.months')}
            />
            <Hr />
            <NegativeMargin />
          </React.Fragment>
        ) : null}
        {filteredCompetitionList &&
          filteredCompetitionList.map((competition: ITeamCompetition, i: number) => {
            // From ITeamCompetition to IMatchDayList
            const {
              competitionId,
              customCompetitionName: title = '',
              date,
              matchList,
              roundGroup,
              round,
              seasonId,
              competitionPhase,
              competitionPhaseRound,
            } = competition;
            const matchDayList: IMatchDayList[] = [
              {
                competitionId,
                competitionPhase,
                competitionPhaseRound,
                date,
                matchList,
                round,
                roundGroup,
                roundName: getRoundName(competitionPhase, round),
                seasonId,
                title,
              },
            ];

            return (
              <ResultsTable
                key={i}
                matchDayList={matchDayList}
                margin={false}
                showMatchRowLiveResult={false}
                trackingData={trackingData}
                type="results-thick"
              />
            );
          })}
      </ErrorBoundary>
    </div>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  TeamCalendarResults.propTypes = {
    competitionFilterModel: PropTypes.arrayOf(
      PropTypes.exact({
        name: PropTypes.string.isRequired,
        secondaryDropdownModel: PropTypes.arrayOf(
          PropTypes.exact({
            label: PropTypes.string.isRequired,
          }).isRequired
        ).isRequired,
      }).isRequired
    ),
    competitionList: PropTypes.arrayOf(
      PropTypes.exact({
        // From src/components/organisms/results-table/results-table.tsx PropTypes
        competitionId: isPropTypeNullable(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
        date: PropTypes.string.isRequired,
        matchList: PropTypes.arrayOf(PropTypes.exact(MatchRow.propTypes!).isRequired).isRequired,
        round: isPropTypeNullable(PropTypes.number),
        roundGroup: isPropTypeNullable(PropTypes.string),
        roundName: isPropTypeNullable(PropTypes.string),
        seasonId: isPropTypeNullable(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
        title: isPropTypeNullable(PropTypes.string),
        // TODO: 2022-02-25: BE will MOVE these PROPS from IMatchRow to IMatchDayList to avoid DUPLICATION in EACH match!
        competitionPhase: PropTypes.oneOf<MatchPhase>(nonLinearCompetitionPhasesArray),
        competitionPhaseRound: PropTypes.oneOf<MatchPhaseRound>(matchPhaseArray),
        // ITeamCompetition
        customCompetitionName: PropTypes.string.isRequired,
        month: PropTypes.string.isRequired,
      }).isRequired
    ).isRequired,
    teamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    trackingData: PropTypes.object,
  };
}

export default TeamCalendarResults;
